import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Check, CreditCard, Home, User, FileText, DollarSign, Gift } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useLanguage } from "@/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OwnerPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [listingId, setListingId] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [freePublishSuccess, setFreePublishSuccess] = useState(false);
  const [userListingInfo, setUserListingInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    owner_name: "",
    owner_email: "",
    owner_phone: "",
    title: "",
    description: "",
    price: "",
    listing_type: "buy",
    property_type: "house",
    bedrooms: "3",
    bathrooms: "2",
    sqft: "",
    address: "",
    city: "",
    province: "QC",
    image_url: "",
    features: "",
    plan: "basic"
  });

  // Check for success return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const returnedListingId = searchParams.get("listing_id");
    
    if (sessionId && returnedListingId) {
      pollPaymentStatus(sessionId, returnedListingId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, listingId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error("Payment status check timed out");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setPaymentSuccess(true);
        setListingId(listingId);
        toast.success(t('ownerPage.success.message'));
      } else if (response.data.status === "expired") {
        toast.error("Payment session expired");
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, listingId, attempts + 1), 2000);
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Check user's listing count when email changes
  const checkUserListingCount = async (email) => {
    if (!email || !email.includes('@')) return;
    try {
      const response = await axios.get(`${API}/owner/listing-count/${encodeURIComponent(email)}`);
      setUserListingInfo(response.data);
    } catch (error) {
      // Silently fail - will check again at submission
    }
  };

  // Check listing count when moving to step 2
  useEffect(() => {
    if (step === 2 && formData.owner_email) {
      checkUserListingCount(formData.owner_email);
    }
  }, [step, formData.owner_email]);

  const validateStep1 = () => {
    return formData.owner_name && formData.owner_email && formData.owner_phone;
  };

  const validateStep2 = () => {
    return formData.title && formData.description && formData.price && 
           formData.address && formData.city && formData.sqft;
  };

  const handleNext = () => {
    if (step === 1 && !validateStep1()) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    if (step === 2 && !validateStep2()) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setStep(prev => prev + 1);
  };

  const handlePrevious = () => {
    setStep(prev => prev - 1);
  };

  const createListing = async () => {
    const listingData = {
      ...formData,
      price: parseFloat(formData.price),
      bedrooms: parseInt(formData.bedrooms),
      bathrooms: parseInt(formData.bathrooms),
      sqft: parseInt(formData.sqft),
      features: formData.features.split(",").map(f => f.trim()).filter(f => f),
      images: formData.image_url ? [formData.image_url] : []
    };

    const response = await axios.post(`${API}/owner/listing`, listingData);
    return response.data;
  };

  const handleFreePublish = async () => {
    setLoading(true);
    try {
      const result = await createListing();
      
      if (result.status === "published" && !result.requires_payment) {
        setFreePublishSuccess(true);
        toast.success(result.message || "Annonce publiée gratuitement !");
      } else if (result.requires_payment) {
        // User has used all free listings, need to pay
        setListingId(result.id);
        toast.info(result.message);
        setUserListingInfo(prev => ({ ...prev, requires_payment: true }));
      }
    } catch (error) {
      toast.error("Erreur lors de la publication");
    } finally {
      setLoading(false);
    }
  };

  const handleStripePayment = async () => {
    setLoading(true);
    try {
      let currentListingId = listingId;
      
      // If no listing created yet, create it first
      if (!currentListingId) {
        const result = await createListing();
        currentListingId = result.id;
        setListingId(currentListingId);
        
        // If it was published for free, show success
        if (result.status === "published" && !result.requires_payment) {
          setFreePublishSuccess(true);
          toast.success(result.message || "Annonce publiée gratuitement !");
          setLoading(false);
          return;
        }
      }

      const response = await axios.post(`${API}/checkout/create`, {
        listing_id: currentListingId,
        plan: formData.plan,
        origin_url: window.location.origin
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error("Erreur lors du paiement");
    } finally {
      setLoading(false);
    }
  };

  const plans = [
    { id: "basic", name: t('ownerPage.plans.basic.name'), price: 12, features: t('ownerPage.plans.basic.features') }
  ];

  const provinces = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];

  // Success page for FREE listings
  if (freePublishSuccess) {
    return (
      <div className="pt-36 pb-20 min-h-screen bg-bone">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-soft">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Gift className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-heading text-3xl font-medium text-charcoal mb-4">
              Annonce publiée gratuitement !
            </h1>
            <p className="text-muted-foreground mb-4">
              Votre propriété est maintenant visible sur notre site.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8">
              <p className="text-green-800 text-sm">
                <strong>🎁 Offre de bienvenue :</strong> Vous avez utilisé {userListingInfo?.active_listings + 1 || 1} de vos 2 annonces gratuites.
                {(userListingInfo?.active_listings + 1 || 1) < 2 && " Il vous reste encore 1 annonce gratuite !"}
              </p>
            </div>
            <div className="flex gap-4 justify-center">
              <Link to="/properties">
                <Button className="rounded-full">{t('ownerPage.success.viewProperties')}</Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="rounded-full">{t('ownerPage.success.backHome')}</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="pt-36 pb-20 min-h-screen bg-bone">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-soft">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-heading text-3xl font-medium text-charcoal mb-4">
              {t('ownerPage.success.title')}
            </h1>
            <p className="text-muted-foreground mb-8">
              {t('ownerPage.success.message')}
            </p>
            <Link to={`/property/${listingId}`}>
              <Button className="rounded-full px-8">
                {t('ownerPage.success.viewProperty')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-36 pb-20 min-h-screen bg-bone" data-testid="owner-page">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-8">
          <Badge className="bg-primary text-white mb-4">{t('ownerCta.badge')}</Badge>
          <h1 className="font-heading text-4xl md:text-5xl font-medium text-charcoal mb-4">
            {t('ownerPage.title')}
          </h1>
          <p className="text-muted-foreground">{t('ownerPage.subtitle')}</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 md:gap-4 mb-8">
          {[
            { num: 1, icon: User, label: t('ownerPage.steps.info') },
            { num: 2, icon: Home, label: t('ownerPage.steps.property') },
            { num: 3, icon: FileText, label: t('ownerPage.steps.plan') },
            { num: 4, icon: DollarSign, label: t('ownerPage.steps.payment') }
          ].map((s, i) => (
            <div key={s.num} className="flex items-center">
              <div className={`flex items-center gap-2 ${step >= s.num ? 'text-primary' : 'text-muted-foreground'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= s.num ? 'bg-primary text-white' : 'bg-secondary'}`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <span className="hidden md:inline text-sm font-medium">{s.label}</span>
              </div>
              {i < 3 && <div className={`w-8 md:w-16 h-0.5 mx-2 ${step > s.num ? 'bg-primary' : 'bg-secondary'}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-soft">
          {/* Step 1: Owner Info */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="font-heading text-2xl font-medium text-charcoal">{t('ownerPage.form.ownerInfo')}</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Input placeholder={t('ownerPage.form.name')} className="rounded-xl" value={formData.owner_name} onChange={(e) => handleInputChange("owner_name", e.target.value)} required />
                <Input type="email" placeholder={t('ownerPage.form.email')} className="rounded-xl" value={formData.owner_email} onChange={(e) => handleInputChange("owner_email", e.target.value)} required />
              </div>
              <Input type="tel" placeholder={t('ownerPage.form.phone')} className="rounded-xl" value={formData.owner_phone} onChange={(e) => handleInputChange("owner_phone", e.target.value)} required />
              <Button onClick={handleNext} className="w-full rounded-full">{t('ownerPage.form.next')}</Button>
            </div>
          )}

          {/* Step 2: Property Info */}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="font-heading text-2xl font-medium text-charcoal">{t('ownerPage.form.propertyInfo')}</h2>
              
              <Input placeholder={t('ownerPage.form.title')} className="rounded-xl" value={formData.title} onChange={(e) => handleInputChange("title", e.target.value)} required />
              
              <Textarea placeholder={t('ownerPage.form.description')} className="rounded-xl min-h-[120px]" value={formData.description} onChange={(e) => handleInputChange("description", e.target.value)} required />
              
              <div className="grid md:grid-cols-2 gap-4">
                <Input type="number" placeholder={t('ownerPage.form.price')} className="rounded-xl" value={formData.price} onChange={(e) => handleInputChange("price", e.target.value)} required />
                
                <Select value={formData.listing_type} onValueChange={(v) => handleInputChange("listing_type", v)}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder={t('ownerPage.form.listingType')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buy">{t('ownerPage.form.sell')}</SelectItem>
                    <SelectItem value="rent">{t('ownerPage.form.rent')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <Select value={formData.property_type} onValueChange={(v) => handleInputChange("property_type", v)}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder={t('ownerPage.form.propertyType')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="house">{t('ownerPage.form.house')}</SelectItem>
                    <SelectItem value="condo">{t('ownerPage.form.condo')}</SelectItem>
                    <SelectItem value="apartment">{t('ownerPage.form.apartment')}</SelectItem>
                    <SelectItem value="chalet">{t('ownerPage.form.chalet')}</SelectItem>
                  </SelectContent>
                </Select>
                
                <Input type="number" placeholder={t('ownerPage.form.bedrooms')} className="rounded-xl" value={formData.bedrooms} onChange={(e) => handleInputChange("bedrooms", e.target.value)} />
                <Input type="number" placeholder={t('ownerPage.form.bathrooms')} className="rounded-xl" value={formData.bathrooms} onChange={(e) => handleInputChange("bathrooms", e.target.value)} />
                <Input type="number" placeholder={t('ownerPage.form.sqft')} className="rounded-xl" value={formData.sqft} onChange={(e) => handleInputChange("sqft", e.target.value)} required />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <Input placeholder={t('ownerPage.form.address')} className="rounded-xl col-span-2" value={formData.address} onChange={(e) => handleInputChange("address", e.target.value)} required />
                <Select value={formData.province} onValueChange={(v) => handleInputChange("province", v)}>
                  <SelectTrigger className="rounded-xl"><SelectValue placeholder={t('ownerPage.form.province')} /></SelectTrigger>
                  <SelectContent>
                    {provinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <Input placeholder={t('ownerPage.form.city')} className="rounded-xl" value={formData.city} onChange={(e) => handleInputChange("city", e.target.value)} required />
              <Input placeholder={t('ownerPage.form.imageUrl')} className="rounded-xl" value={formData.image_url} onChange={(e) => handleInputChange("image_url", e.target.value)} />
              <Input placeholder={t('ownerPage.form.features')} className="rounded-xl" value={formData.features} onChange={(e) => handleInputChange("features", e.target.value)} />

              <div className="flex gap-4">
                <Button variant="outline" onClick={handlePrevious} className="flex-1 rounded-full">{t('ownerPage.form.previous')}</Button>
                <Button onClick={handleNext} className="flex-1 rounded-full">{t('ownerPage.form.next')}</Button>
              </div>
            </div>
          )}

          {/* Step 3: Choose Plan - Shows FREE option if available */}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="font-heading text-2xl font-medium text-charcoal">{t('ownerPage.form.choosePlan')}</h2>
              
              {/* Free listings info banner */}
              {userListingInfo && !userListingInfo.requires_payment && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <Gift className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">🎁 Offre de bienvenue</p>
                      <p className="text-sm text-green-700">
                        Vous avez {userListingInfo.free_remaining} annonce(s) gratuite(s) restante(s) sur {userListingInfo.free_limit}.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {userListingInfo && userListingInfo.requires_payment && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-8 h-8 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">Annonces gratuites épuisées</p>
                      <p className="text-sm text-amber-700">
                        Vous avez déjà publié {userListingInfo.active_listings} annonces. Un paiement de 12$/mois est requis pour cette annonce.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Show FREE option if available */}
              {(!userListingInfo || !userListingInfo.requires_payment) && (
                <div
                  className="border-2 border-green-500 bg-green-50 rounded-2xl p-6 cursor-pointer"
                >
                  <Badge className="bg-green-500 text-white mb-2">GRATUIT</Badge>
                  <h3 className="font-heading text-xl font-medium text-charcoal mb-2">Annonce Gratuite</h3>
                  <p className="text-3xl font-bold text-green-600 mb-1">0$</p>
                  <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" />1 annonce</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" />Photos standard</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" />Durée illimitée</li>
                  </ul>
                </div>
              )}

              {/* Show paid plan if free is not available */}
              {userListingInfo && userListingInfo.requires_payment && (
                <div className="grid md:grid-cols-1 gap-4">
                  {plans.map(plan => (
                    <div
                      key={plan.id}
                      onClick={() => handleInputChange("plan", plan.id)}
                      className={`border-2 rounded-2xl p-6 cursor-pointer transition-all ${formData.plan === plan.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                    >
                      <h3 className="font-heading text-xl font-medium text-charcoal mb-2">{plan.name}</h3>
                      <p className="text-3xl font-bold text-primary mb-1">${plan.price}<span className="text-sm font-normal text-muted-foreground">{t('ownerPage.form.perMonth')}</span></p>
                      <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                        {plan.features.map((f, i) => <li key={`${plan.id}-feature-${i}`} className="flex items-center gap-2"><Check className="w-4 h-4 text-primary" />{f}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-4">
                <Button variant="outline" onClick={handlePrevious} className="flex-1 rounded-full">{t('ownerPage.form.previous')}</Button>
                <Button onClick={handleNext} className="flex-1 rounded-full">{t('ownerPage.form.next')}</Button>
              </div>
            </div>
          )}

          {/* Step 4: Payment or Free Publish */}
          {step === 4 && (
            <div className="space-y-6">
              {/* FREE PUBLISH - If user still has free listings */}
              {(!userListingInfo || !userListingInfo.requires_payment) ? (
                <>
                  <h2 className="font-heading text-2xl font-medium text-charcoal">Confirmer la publication</h2>
                  
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-charcoal font-medium">Annonce Gratuite</span>
                      <span className="text-2xl font-bold text-green-600">0$</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{formData.title || "Votre annonce"}</p>
                    <div className="mt-4 pt-4 border-t border-green-200">
                      <p className="text-sm text-green-700">
                        <Gift className="w-4 h-4 inline mr-1" />
                        Publication gratuite - {userListingInfo?.free_remaining || 2} annonce(s) gratuite(s) restante(s)
                      </p>
                    </div>
                  </div>

                  <Button 
                    onClick={handleFreePublish} 
                    className="w-full rounded-full h-14 text-lg bg-green-600 hover:bg-green-700"
                    disabled={loading}
                  >
                    <Check className="w-5 h-5 mr-2" />
                    {loading ? "Publication en cours..." : "Publier gratuitement"}
                  </Button>

                  <Button variant="outline" onClick={handlePrevious} className="w-full rounded-full">{t('ownerPage.form.previous')}</Button>
                </>
              ) : (
                <>
                  {/* PAID PUBLISH - User has used all free listings */}
                  <h2 className="font-heading text-2xl font-medium text-charcoal">{t('ownerPage.steps.payment')}</h2>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                    <p className="text-sm text-amber-800">
                      <strong>⚠️ Annonces gratuites épuisées</strong><br/>
                      Vous avez déjà publié {userListingInfo?.active_listings || 2} annonces gratuites. 
                      Le paiement est requis pour publier cette annonce.
                    </p>
                  </div>
                  
                  <div className="bg-secondary/50 rounded-xl p-6">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-charcoal font-medium">{plans.find(p => p.id === formData.plan)?.name}</span>
                      <span className="text-2xl font-bold text-primary">${plans.find(p => p.id === formData.plan)?.price}{t('ownerPage.form.perMonth')}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{formData.title || "Votre annonce"}</p>
                  </div>

                  <div className="space-y-4">
                    <Button 
                      onClick={handleStripePayment} 
                      className="w-full rounded-full h-14 text-lg"
                      disabled={loading}
                    >
                      <CreditCard className="w-5 h-5 mr-2" />
                      {loading ? t('ownerPage.form.processing') : t('ownerPage.form.payWithStripe')}
                    </Button>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border"></div></div>
                      <div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-muted-foreground">ou</span></div>
                    </div>

                    <a 
                      href="https://www.paypal.com/ncp/payment/SQPSBAGRQKCJW" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-full"
                    >
                      <Button variant="outline" className="w-full rounded-full h-14 text-lg bg-[#FFC439] hover:bg-[#f0b72e] text-[#003087] border-[#FFC439]">
                        Payer avec PayPal
                      </Button>
                    </a>

                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                      <p className="text-sm text-blue-800">
                        <strong>📌 Instructions PayPal :</strong><br/>
                        1. Cliquez sur "Payer avec PayPal"<br/>
                        2. Effectuez votre paiement<br/>
                        3. Envoyez votre confirmation à <strong>contact@dreamhousecanada.org</strong> avec les détails de votre propriété<br/>
                        4. Nous publierons votre annonce sous 24h
                      </p>
                    </div>
                  </div>

                  <Button variant="outline" onClick={handlePrevious} className="w-full rounded-full">{t('ownerPage.form.previous')}</Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OwnerPage;
